import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - PG官网',
  description: 'PG官网是PG游戏的官方正版平台，致力于为玩家提供安全、稳定、优质的游戏体验。了解PG官网的品牌背景、信誉保障和联系方式。',
  url: 'https://www.pg-official.com/about',
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PG官网',
  url: 'https://www.pg-official.com/',
  description: 'PG官方正版游戏平台，提供安全可靠的游戏体验。',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-XXXX',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
    areaServed: 'CN',
  },
  email: 'support@pg-official.com',
  foundingDate: '2018',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const phoneAlt = formData.get('phone_alt');
    if (phoneAlt && String(phoneAlt).trim() !== '') {
      setFormStatus('success');
      setFormError('');
      return;
    }
    setFormStatus('loading');
    setFormError('');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9sqg4h4ne17ei0sdm7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (e) { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch (err) {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    } finally {
      // keep status
    }
  }, []);

  return (
    <>
      <SEOHead
        title="关于我们 - PG官网 | 品牌介绍与联系方式"
        description="PG官网是PG游戏的官方正版平台，致力于为玩家提供安全、稳定、优质的游戏体验。了解PG官网的品牌背景、信誉保障和联系方式。"
        canonicalUrl="https://www.pg-official.com/about"
        schema={{ ...schema, ...aboutSchema }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero Banner */}
        <section className="relative w-full h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20professional%20gaming%20company%20headquarters%20with%20warm%20lighting%2C%20glass%20building%20architecture%2C%20elegant%20corporate%20atmosphere%2C%20golden%20hour%20sunlight&width=1920&height=600&seq=about-pg-hero&orientation=landscape"
            alt="关于PG官网"
            title="关于我们 - PG官网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-4xl font-bold text-white font-heading mb-3">
              <a href="/about" className="text-white">关于我们</a>
            </h1>
            <p className="text-white/70 max-w-xl mx-auto text-sm md:text-base">
              PG官网 — 值得信赖的官方正版游戏平台
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  <strong>PG官网</strong>是PG游戏官方授权的正版游戏平台。自成立以来，我们始终坚持以玩家为中心，致力于为广大游戏爱好者提供一个<strong>安全、稳定、公平</strong>的游戏环境。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  PG官网汇聚了丰富多样的游戏种类，从紧张刺激的<strong>动作游戏</strong>到深度沉浸的<strong>角色扮演</strong>，从考验智慧的<strong>策略游戏</strong>到轻松愉快的<strong>休闲游戏</strong>，满足不同玩家的多元化需求。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  作为<strong>PG官网登录入口</strong>的官方渠道，我们确保每一位玩家都能通过安全的方式访问平台，享受正版游戏带来的纯粹乐趣。
                </p>
              </div>
              <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20gaming%20studio%20office%20with%20developers%20working%20on%20computers%2C%20warm%20ambient%20lighting%2C%20creative%20workspace%2C%20professional%20tech%20company%20atmosphere&width=800&height=600&seq=about-brand&orientation=portrait"
                  alt="PG官网品牌形象"
                  title="PG官网 - 品牌介绍"
                  className="w-full h-full object-cover object-top rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation & Trust */}
        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8 text-center">
              信誉保障
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '官方正版授权',
                  desc: 'PG官网持有PG游戏官方正版授权，确保所有游戏资源均为正版，杜绝盗版和外挂风险。',
                },
                {
                  icon: 'ri-lock-line',
                  title: '数据安全保障',
                  desc: '采用银行级别SSL加密协议，严格保护玩家的个人信息和交易数据安全。',
                },
                {
                  icon: 'ri-scales-line',
                  title: '公平游戏环境',
                  desc: '先进的AI反作弊系统7×24小时运行，确保每一位玩家都在公平的环境中竞技。',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '优质客服支持',
                  desc: '专业客服团队全天候在线，30分钟内响应每一位玩家的咨询和反馈。',
                },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-4">
                    <i className={`${item.icon} text-accent-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="flex flex-col gap-5">
                  {[
                    { icon: 'ri-mail-line', label: '官方邮箱', value: 'support@pg-official.com' },
                    { icon: 'ri-phone-line', label: '客服热线', value: '400-888-XXXX' },
                    { icon: 'ri-time-line', label: '服务时间', value: '7×24小时全天候服务' },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: '中国上海市浦东新区XX路XX号' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className={`${item.icon} text-primary-500 text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-sm text-foreground-500 mb-0.5">{item.label}</p>
                        <p className="text-sm font-semibold text-foreground-900">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">在线留言</h3>
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="website-alt-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm text-foreground-600 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm text-foreground-600 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm text-foreground-600 mb-1">电话</label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电话（选填）"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm text-foreground-600 mb-1">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">留言已成功提交，我们会尽快与您联系!</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}